// D02 Unit 03 — 802.1Q Trunks (LAB)
import { observe, readConfirm } from '../../ui/widgets.js';
import { twoSwitch, twoSwitchLayout } from './topologies.js';
import { ran, pinged, iosPinged, reach, isTrunk, portState } from './helpers.js';
import { fmtVlanList } from '../../engine/ios/switching.js';

const layout = { ...twoSwitchLayout, linkNotes: { 'SW1:GigabitEthernet0/1--SW2:GigabitEthernet0/1': 'to be configured: 802.1Q trunk' } };
const T = { native: 99, allowed: [10, 20, 99] };

export default {
  id: 'u03', num: '03', kind: 'LAB', title: '802.1Q Trunks', type: 'Configuration Lab', implemented: true,
  objective: 'Configure an 802.1Q trunk between two switches with a dedicated native VLAN, and prove every VLAN crosses it.',
  network: () => twoSwitch({ trunkUp: false }),
  layout, linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'Tagging frames between switches', workspace: false,
      content: `
<p>An access port carries one VLAN. A link between two switches needs to carry <b>all</b> of them, so switches add an <b>802.1Q tag</b> (4 bytes, with a 12-bit VLAN ID) to each frame on a <b>trunk</b>.</p>
<pre>| Dest MAC | Src MAC | 802.1Q: TPID 0x8100 · PRI · VID=20 | Type | Payload | FCS |</pre>
<ul><li>Frames in the <b>native VLAN</b> cross the trunk <b>untagged</b>. Both ends must agree on it.</li>
<li>Best practice: a dedicated, unused native VLAN (here VLAN 99), never VLAN 1.</li>
<li>Hosts never see tags: the egress access port removes them.</li>
<li>This lab uses static trunks (<code>switchport mode trunk</code>). DTP negotiation is left off in production designs.</li></ul>`,
      widgets: [readConfirm({ id: 'd2u03-read' })],
      question: 'PC1 on SW1 and PC3 on SW2 are both in VLAN 10. What happens to PC1\'s ARP broadcast today, with the uplink as an access port in VLAN 1?',
    },
    {
      id: 'build', phase: 'BUILD', title: 'Build the trunk', devices: ['SW1', 'SW2', 'PC1', 'PC2'],
      ticket: { id: '2003', priority: 3, from: 'Floor 3 Sales', subject: 'Floor 3 cannot reach Floor 2 colleagues', body: 'SW2 was installed yesterday. Sales and Support on floor 3 cannot reach their colleagues on floor 2.' },
      content: `<p>Configure Gi0/1 on both switches from the design:</p>
<table><tr><th>Setting</th><th>Value</th></tr><tr><td>Mode</td><td>static trunk (802.1Q)</td></tr><tr><td>Native VLAN</td><td>99 (MGMT)</td></tr><tr><td>Allowed VLANs</td><td>10, 20, 99</td></tr></table>
<pre>interface gi0/1
 switchport mode trunk
 switchport trunk native vlan 99
 switchport trunk allowed vlan 10,20,99</pre>`,
      evidence: [
        { id: 't1', text: 'SW1: trunk status captured', match: ran('SW1', 'show interfaces trunk', /Gi0\/1\s+on\s+802\.1q\s+trunking\s+99/) },
        { id: 't2', text: 'SW2: trunk status captured', match: ran('SW2', 'show interfaces trunk', /Gi0\/1\s+on\s+802\.1q\s+trunking\s+99/) },
        { id: 'v10', text: 'PC1: reaches PC3 across the trunk (VLAN 10)', match: pinged('PC1', '10.10.10.21', { success: true }) },
        { id: 'v20', text: 'PC2: reaches PC4 across the trunk (VLAN 20)', match: pinged('PC2', '10.10.20.21', { success: true }) },
        { id: 'mgmt', text: 'SW1: management ping to SW2 (VLAN 99)', match: iosPinged('SW1', '10.10.99.12', true) },
      ],
      widgets: [observe({
        id: 'd2u03-obs', title: 'READ THE TRUNK',
        fields: [
          { label: 'SW1 Gi0/1: VLANs in spanning tree forwarding state and not pruned', check: (v, rt) => { const f = [10, 20, 99].filter((x) => portState(rt, x, 'SW1', 'GigabitEthernet0/1')?.state === 'FWD'); return v.replace(/\s/g, '') === fmtVlanList(f) || 'Copy the last section of show interfaces trunk.'; } },
          { label: 'Which VLAN crosses this trunk untagged?', check: (v) => v.trim() === '99' },
        ],
      })],
      tasks: [
        { id: 'sw1', text: 'SW1 Gi0/1 is a trunk, native 99, allowing 10,20,99', check: (rt) => isTrunk(rt, 'SW1', 'GigabitEthernet0/1', T), fail: 'SW1 Gi0/1 does not match the design.' },
        { id: 'sw2', text: 'SW2 Gi0/1 is a trunk, native 99, allowing 10,20,99', check: (rt) => isTrunk(rt, 'SW2', 'GigabitEthernet0/1', T), fail: 'SW2 Gi0/1 does not match the design.' },
        { id: 'e2e', text: 'Both VLANs and management work across floors', check: (rt) => reach(rt, 'PC1', '10.10.10.21') && reach(rt, 'PC2', '10.10.20.21') && reach(rt, 'SW1', '10.10.99.12'), fail: 'At least one VLAN does not cross the trunk.' },
      ],
      question: 'After you configure only SW1, the link still works for some VLANs. Which one, and why?',
      hints: ['Configure both ends — a trunk facing an access port is a mismatch.', 'The management SVIs are in VLAN 99, the native VLAN. <code>ping 10.10.99.12</code> from SW1 proves it.'],
    },
  ],
  review: () => '<p>Tagged VLANs 10 and 20 and the untagged native VLAN 99 now all cross one link. Next: what happens when the two ends disagree.</p>',
};
